<template>
  <div v-show="isVisible">
    <div class="contact-filter-field" @click="openFiltersModal">
      <template v-if="hasFilters">
        <div class="contact-filter-field__label">
          {{ schema.title || schema.label || 'Contact Filters' }}
        </div>
        <div class="contact-filter-field__conditions">
          <div
            v-for="(desc, idx) in conditionDescriptions"
            :key="idx"
            class="contact-filter-field__condition"
          >
            <v-icon x-small class="mr-1" color="appMain"> filter_list </v-icon>
            <span class="contact-filter-field__condition-text">
              {{ desc }}
            </span>
          </div>
          <div v-if="hasOverflow" class="contact-filter-field__overflow">
            +{{ overflowCount }} more
          </div>
        </div>
        <div class="contact-filter-field__actions">
          <span class="contact-filter-field__match-type">
            {{ matchTypeLabel }}
          </span>
          <v-btn
            icon
            x-small
            class="contact-filter-field__clear"
            @click.stop="clearFilters"
          >
            <v-icon small>close</v-icon>
          </v-btn>
        </div>
      </template>
      <div v-else class="contact-filter-field__empty">
        <v-icon small class="mr-2">filter_list</v-icon>
        <span class="contact-filter-field__placeholder">
          {{ schema.title || schema.label || 'Contact Filters' }}
        </span>
      </div>
    </div>

    <contacts-viewer-button
      v-if="hasFilters && showCount"
      :filters="localValue"
      class="mt-1"
    >
      <template v-if="contactCount !== null">
        {{ contactCount }} contact(s)
      </template>
      <template v-else>View contacts</template>
    </contacts-viewer-button>

    <group-form-modal
      v-if="isFiltersModalOpen"
      v-model="isFiltersModalOpen"
      :triggerGroup="triggerGroup"
      :triggerOnly="true"
      :groupName="triggerGroup.name"
      isModal
      @groupSaved="onFiltersSaved"
    />
  </div>
</template>

<script>
import GenericField from './GenericField';
import GroupFormModal from '@/views/groups/form/GroupFormModal.vue';
import ContactsViewerButton from '@/views/contacts/components/contactsViewer/ContactsViewerButton.vue';
import isEmpty from 'lodash/isEmpty';

const MAX_VISIBLE_CONDITIONS = 3;

const OPERATOR_LABELS = {
  $eq: 'is',
  $ne: 'is not',
  $in: 'includes',
  $nin: 'excludes',
  $gt: '>',
  $gte: '>=',
  $lt: '<',
  $lte: '<=',
  $exists: 'exists',
  $regex: 'contains',
};

export default {
  name: 'ContactFilterField',
  mixins: [GenericField],
  components: {
    GroupFormModal,
    ContactsViewerButton,
  },
  data() {
    return {
      isFiltersModalOpen: false,
      contactCount: null,
    };
  },
  computed: {
    hasFilters() {
      return this.localValue && !isEmpty(this.localValue);
    },

    conditions() {
      if (!this.hasFilters) return [];
      const filters = this.localValue || {};
      const items = [];

      if (filters.$and) items.push(...filters.$and);
      if (filters.$or) items.push(...filters.$or);

      const records = filters.$records;
      if (records) {
        if (Array.isArray(records)) {
          records.forEach((group) => {
            items.push({ _type: 'records', _group: group });
          });
        } else if (typeof records === 'object') {
          const subGroups = records.$or || records.$and || [];
          subGroups.forEach((group) => {
            items.push({ _type: 'records', _group: group });
          });
        }
      }

      Object.keys(filters).forEach((key) => {
        if (!key.startsWith('$')) {
          items.push({ [key]: filters[key] });
        }
      });

      return items;
    },

    conditionDescriptions() {
      return this.conditions
        .slice(0, MAX_VISIBLE_CONDITIONS)
        .map((condition) => this.describeCondition(condition));
    },

    hasOverflow() {
      return this.conditions.length > MAX_VISIBLE_CONDITIONS;
    },

    overflowCount() {
      return this.conditions.length - MAX_VISIBLE_CONDITIONS;
    },

    matchTypeLabel() {
      if (!this.hasFilters) return '';
      const filters = this.localValue || {};
      const hasAnd = (filters.$and || []).length > 0;
      const hasOr = (filters.$or || []).length > 0;
      const hasRecords = !!filters.$records;

      if (hasRecords && !hasAnd && !hasOr) return 'Activity filter';
      if (hasAnd && !hasOr && !hasRecords) return 'Match all';
      if (hasOr && !hasAnd && !hasRecords) return 'Match any';
      if (hasAnd || hasOr || hasRecords) return 'Mixed';
      return '';
    },

    showCount() {
      return this.schema.showCount !== false;
    },

    triggerGroup() {
      return {
        id: 1,
        name: this.schema.filterName || this.schema.label || 'Contact Filters',
        filters: this.localValue || {},
      };
    },
  },
  methods: {
    openFiltersModal() {
      this.isFiltersModalOpen = true;
    },

    onFiltersSaved(group) {
      this.localValue = group.filters;
      this.isFiltersModalOpen = false;
    },

    clearFilters() {
      this.localValue = null;
      this.contactCount = null;
    },

    describeCondition(condition) {
      if (!condition || typeof condition !== 'object') return 'Filter';

      if (condition._type === 'records') {
        return this.describeRecordsGroup(condition._group);
      }

      const keys = Object.keys(condition);
      if (!keys.length) return 'Filter';

      const fieldKey = keys[0];
      const fieldName = this.formatFieldName(fieldKey);
      const operatorObj = condition[fieldKey];

      if (!operatorObj || typeof operatorObj !== 'object') {
        const displayValue = this.formatValue(operatorObj);
        return `${fieldName} is ${displayValue}`;
      }

      const opKey = Object.keys(operatorObj)[0];
      const opLabel = OPERATOR_LABELS[opKey] || opKey;
      const rawValue = operatorObj[opKey];
      const displayValue = this.formatValue(rawValue);

      return `${fieldName} ${opLabel} ${displayValue}`;
    },

    describeRecordsGroup(group) {
      if (!Array.isArray(group) || !group.length) return 'Activity filter';

      const eventCondition = group.find(
        (c) => c && typeof c === 'object' && c.event
      );
      if (eventCondition) {
        const eventOp = eventCondition.event;
        const eventValue =
          typeof eventOp === 'object' ? Object.values(eventOp)[0] : eventOp;
        const label = String(eventValue).replace(/_/g, ' ');
        return `Activity: ${label}`;
      }

      return `Activity filter (${group.length} conditions)`;
    },

    formatFieldName(key) {
      const parts = key.split('.');
      const name = parts.length > 1 ? parts.slice(1).join('.') : key;
      return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },

    formatValue(val) {
      if (Array.isArray(val)) {
        if (val.length <= 2) return val.join(', ');
        return `${val[0]}, ${val[1]} +${val.length - 2}`;
      }
      if (val === true || val === 'true') return 'yes';
      if (val === false || val === 'false') return 'no';
      if (val === null || val === undefined) return '';
      const str = String(val);
      return str.length > 20 ? str.slice(0, 20) + '...' : str;
    },
  },
};
</script>

<style lang="less" scoped>
.contact-filter-field {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 8px 12px;
  border-radius: @base-radius;
  background-color: #fff;
  box-shadow: @content-shadow;
  cursor: pointer;
  min-height: 40px;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  }

  &__label {
    width: 100%;
    font-size: 10px;
    line-height: 1;
    color: var(--v-dark-base);
    opacity: 0.55;
    margin-bottom: 6px;
  }

  &__conditions {
    width: 100%;
  }

  &__condition {
    display: flex;
    align-items: center;
    padding: 2px 0;
    font-size: 13px;
    line-height: 1.3;
    color: var(--v-dark-base);

    .v-icon {
      flex-shrink: 0;
    }
  }

  &__condition-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__overflow {
    font-size: 12px;
    color: var(--v-dark-base);
    opacity: 0.55;
    padding: 2px 0 0 20px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  &__match-type {
    font-size: 11px;
    color: var(--v-appMain-base);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__clear {
    opacity: 0.4;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 1;
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    width: 100%;
    color: var(--v-dark-base);
    opacity: 0.55;

    .v-icon {
      color: var(--v-dark-base) !important;
    }
  }

  &__placeholder {
    font-size: 14px;
  }
}
</style>
